/* Administración (pantalla 6c) — quién entra, qué puede ver y cuánto está costando.
 *
 * Tras `es_admin`, que es un ROL y no una capacidad: las capacidades se reparten desde esta misma
 * pantalla, y una de ellas nunca puede abrir la puerta de quien las reparte.
 *
 * ⚠ Las capacidades que se enseñan por cuenta son las EFECTIVAS, no las casillas guardadas. Un
 * usuario con alcance asignado nunca tiene asistente —la regla vive en `Usuario.puede()` del
 * servidor, no aquí— y enseñar la casilla marcada haría creer a quien administra que esa cuenta
 * tiene chat, cuando recibe 403 al abrirlo. La pantalla lo DICE, en vez de dejarlo implícito.
 *
 * Y los nombres de las capacidades se PIDEN al servidor. Una lista de seis rótulos copiada aquí se
 * desincroniza en cuanto se añade una, y el síntoma sería un permiso que existe y que nadie puede
 * otorgar porque no aparece en el formulario.
 */
import { useCallback, useEffect, useState } from 'react'
import { enviar, ErrorApi, obtener } from '../api/cliente'
import { entero, fecha, normalizarNombre, porcentaje } from '../api/formato'
import { useSesion } from '../estado/sesion'
import { IconoCandado, IconoMas, IconoNo, IconoSi } from '../ui/Iconos'
import './Administracion.css'

type Cap = { clave: string; titulo: string; cuesta: boolean }
type UsuarioAdmin = {
  id: number
  usuario: string
  nombre: string
  correo: string
  es_admin: boolean
  activo: boolean
  cambiar_clave: boolean
  alcance: { id: number; nombre: string } | null
  ultimo_acceso: number | null
  empresas: number
  caps: Record<string, boolean>
  caps_guardadas: Record<string, boolean>
}
type AlcanceAdmin = {
  id: number
  nombre: string
  descripcion: string
  cuentas: number
  filtro: {
    incluir?: { unspsc?: string[]; texto?: string[]; sectores?: string[] }
    excluir?: { unspsc?: string[]; texto?: string[] }
    restringir?: Record<string, unknown>
  }
}
type Prueba = {
  total: number
  universo: number
  muestra: number
  vias: Record<string, number>
  ejemplos: { unspsc: string; motivo: string; objeto: string; entidad: string }[]
}
type Uso = {
  hay_registro: boolean
  dias?: number
  usd?: number
  lineas?: number
  por_modelo?: Record<string, number>
  usd_por_llamada?: number | null
  cache_hit_pct?: number | null
}
type EstadoDatos = {
  generado_en?: string
  corte_grafo?: string
  procesos?: number
  entidades?: number
  nuevos?: number
  cambiados?: number
  semantica?: {
    activa?: boolean
    estado?: string
    cobertura?: number | null
    caminos?: { historial?: boolean; rup_categorias?: boolean }
    remedio?: string | null
  }
}

type Pestaña = 'usuarios' | 'alcances' | 'uso'

/** El estado del match semántico, dicho con precisión.
 *
 * Antes esto era un solo aviso —«está apagado»— para tres situaciones muy distintas, y la más
 * común de las tres era la menos grave: los vectores estaban ahí y solo faltaba declararlos. Un
 * aviso que no distingue entre «no hay nada» y «hay todo pero sin sellar» manda a regenerar
 * artefactos que están perfectos. Ahora cada estado dice qué pasa y a quién le afecta. */
function AvisoSemantica({ s }: { s: NonNullable<EstadoDatos['semantica']> }) {
  if (s.activa && s.estado === 'ok') {
    const parcial = typeof s.cobertura === 'number' && s.cobertura < 1
    if (!parcial) return null
    return (
      <p className="admin-aviso">
        El match semántico está activo, pero solo{' '}
        <b>{porcentaje(s.cobertura, { fraccion: true })} de los procesos tiene vector</b>. Los que no
        lo tienen se ordenan sin esa señal. Se completa con el próximo build en una máquina con GPU.
      </p>
    )
  }
  if (s.activa) {
    const cae = s.caminos?.historial === false
    return (
      <p className="admin-aviso">
        El match semántico funciona <b>a medias</b>. {cae
          ? 'Las empresas con historial en SECOP se están ordenando con los centroides de su RUP, no con sus propios contratos.'
          : 'El arranque en frío por categorías del RUP no está disponible.'}
        <Remedio cmd={s.remedio} />
      </p>
    )
  }
  return (
    <p className="admin-aviso">
      El <b>match semántico está apagado</b> ({s.estado}). La afinidad se está calculando por sector
      y código, sin la señal más precisa
      {s.estado === 'sin_vectores'
        ? ' — el cubo se construyó sin vectores, casi siempre porque el servidor se quedó sin torch.'
        : ' — los artefactos no vienen todos del mismo modelo.'}
      <Remedio cmd={s.remedio} />
    </p>
  )
}

/** El comando exacto, no «regenera los artefactos».
 *
 * Lo calcula `espacio.remedio()` en el servidor y arranca en el eslabón roto, no antes: mandar
 * re-embeber 2,8 millones de objetos cuando solo hace falta reconstruir el cubo es la clase de
 * consejo que se aprende a ignorar, y entonces la guarda entera deja de servir. */
function Remedio({ cmd }: { cmd?: string | null }) {
  if (!cmd) return null
  return (
    <>
      {' '}Se arregla corriendo en el servidor: <code className="admin-cmd">{cmd}</code>
    </>
  )
}

export function Administracion() {
  const { yo } = useSesion()
  const [pestaña, setPestaña] = useState<Pestaña>('usuarios')
  const [caps, setCaps] = useState<Cap[]>([])
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([])
  const [alcances, setAlcances] = useState<AlcanceAdmin[]>([])
  const [uso, setUso] = useState<Uso | null>(null)
  const [datos, setDatos] = useState<EstadoDatos | null>(null)
  const [error, setError] = useState<string | null>(null)

  /* Lo que cambia al guardar: las cuentas y sus alcances. Cada escritura llama a ESTO y no a la
     carga entera — `/api/admin/uso` reparse `logs/cost.log` completo (un archivo que solo crece)
     y el vocabulario de capacidades es constante mientras el proceso viva. Recargarlos tras
     marcar una casilla es trabajo que nadie pidió, y crece con el registro. */
  const cargarCuentas = useCallback(async () => {
    try {
      const [u, a] = await Promise.all([
        obtener<{ usuarios: UsuarioAdmin[] }>('/api/admin/usuarios'),
        obtener<{ alcances: AlcanceAdmin[] }>('/api/admin/alcances'),
      ])
      setUsuarios(u.usuarios)
      setAlcances(a.alcances)
      setError(null)
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void cargarCuentas()
    obtener<{ caps: Cap[] }>('/api/admin/vocabulario')
      .then((v) => setCaps(v.caps))
      .catch((e) => setError(e instanceof ErrorApi ? e.message : String(e)))
    // Estas dos por separado: si el cubo no está o el registro de costos no existe, la lista de
    // usuarios se ve igual. Una pantalla de administración a medias sirve; una que no carga, no.
    obtener<Uso>('/api/admin/uso').then(setUso).catch(() => setUso(null))
    obtener<EstadoDatos>('/api/admin/estado-datos').then(setDatos).catch(() => setDatos(null))
  }, [cargarCuentas])

  /* Mismo mensaje que una ruta inexistente, y a propósito: `deps.exige_admin` responde 404 y no
     403 para que quien no administra ni siquiera se entere de que la sección existe. Decir aquí
     «esto es de administración» desharía esa decisión en la única superficie donde se ve. */
  if (!yo?.es_admin) {
    return (
      <div className="estado-pagina">
        <h1>Esa página no existe</h1>
        <p>
          Puede que el enlace esté incompleto. <a href="/">Volver a las oportunidades</a>
        </p>
      </div>
    )
  }

  return (
    <div className="admin">
      <div className="admin-cuerpo">
        <header className="admin-cabeza">
          <div>
            <h1>Administración</h1>
            <p>Quién entra, qué puede ver cada cuenta y cuánto está costando la IA.</p>
          </div>
        </header>

        <nav className="admin-pestañas" aria-label="Secciones">
          {([['usuarios', `Usuarios · ${entero(usuarios.length)}`],
             ['alcances', `Alcances de datos · ${entero(alcances.length)}`],
             ['uso', 'Uso y costos']] as [Pestaña, string][]).map(([k, t]) => (
            <button type="button" key={k} aria-pressed={pestaña === k}
                    className={`admin-pestaña${pestaña === k ? ' activa' : ''}`}
                    onClick={() => setPestaña(k)}>
              {t}
            </button>
          ))}
        </nav>

        {error && <p className="admin-error" role="alert">{error}</p>}

        <div className="admin-columnas">
          <section className="admin-principal">
            {pestaña === 'usuarios' && (
              <Usuarios usuarios={usuarios} caps={caps} alcances={alcances}
                        alRecargar={cargarCuentas} />
            )}
            {pestaña === 'alcances' && <Alcances alcances={alcances} />}
            {pestaña === 'uso' && <UsoYCostos uso={uso} />}
          </section>

          <aside className="admin-lateral">
            <EstadoDeLosDatos datos={datos} />
            <ResumenUso uso={uso} />
          </aside>
        </div>
      </div>
    </div>
  )
}

/* ── usuarios ─────────────────────────────────────────────────────────────── */

/** Mensaje legible de un fallo de la API, para pintarlo junto al formulario que lo causó. */
function mensajeDe(e: unknown): string {
  return e instanceof ErrorApi ? e.message : String(e)
}

/** La contraseña temporal, dicha UNA vez. El servidor no la guarda en claro, así que esta caja es
 *  la única oportunidad de copiarla — por eso no se cierra sola ni la tapa una recarga de la
 *  lista, y solo la quita quien la leyó. */
function ClaveTemporal({ usuario, clave, alCerrar }: {
  usuario: string
  clave: string
  alCerrar: () => void
}) {
  return (
    <div className="clave-caja" role="alert">
      <p>
        Contraseña temporal de <b>{usuario}</b>: <code className="clave-valor">{clave}</code>
      </p>
      <p className="clave-aviso">
        Entrégasela y pídele que la cambie al entrar. <b>No volverá a mostrarse.</b>
      </p>
      <button type="button" className="accion" onClick={alCerrar}>Ya la entregué</button>
    </div>
  )
}

type PropsUsuarios = {
  usuarios: UsuarioAdmin[]
  caps: Cap[]
  alcances: AlcanceAdmin[]
  alRecargar: () => Promise<void>
}

function Usuarios({ usuarios, caps, alcances, alRecargar }: PropsUsuarios) {
  return (
    <>
      {/* La regla del sistema, ESCRITA. No es una nota de cortesía: explica por qué una casilla
          marcada puede no dar permiso, que es la pregunta que hace quien administra al ver que una
          cuenta con alcance no puede abrir el asistente. */}
      <div className="admin-regla">
        <span className="rotulo">Reglas del sistema</span>
        <ul>
          <li>
            <IconoCandado />
            <span>
              Una cuenta con <b>alcance de datos</b> nunca tiene asistente. El asistente consulta el
              grafo completo, así que dárselo anularía su alcance por entero — la regla se aplica en
              el servidor, no solo en este formulario.
            </span>
          </li>
          <li>
            <IconoSi />
            <span>
              <b>Ve todo</b> es no asignarle alcance. Un alcance vacío no muestra nada: no es el
              estado por omisión, es una restricción total.
            </span>
          </li>
          <li>
            <IconoNo />
            <span>
              Las capacidades marcadas con <b>·$</b> gastan dinero en cada uso. Repartirlas es una
              decisión de presupuesto, no solo de permisos.
            </span>
          </li>
        </ul>
      </div>

      <NuevaCuenta alcances={alcances} alRecargar={alRecargar} />

      <ul className="cuentas">
        {usuarios.map((u) => (
          <Cuenta key={u.id} u={u} caps={caps} alcances={alcances} alRecargar={alRecargar} />
        ))}
      </ul>

      {/* Lo que esta consola todavía NO hace, dicho aquí y no descubierto al buscarlo. */}
      <p className="descargo">
        Los alcances se crean y se editan, y las sesiones, la auditoría y las calificaciones se
        revisan, en la <a href="/admin/usuarios">consola anterior</a>.
      </p>
    </>
  )
}

/** Crear una cuenta. Sin campo de contraseña a propósito: la temporal la genera el SERVIDOR y se
 *  muestra una única vez — igual que en la consola anterior—, así nadie elige por otra persona
 *  una clave que ya conoce. */
function NuevaCuenta({ alcances, alRecargar }: {
  alcances: AlcanceAdmin[]
  alRecargar: () => Promise<void>
}) {
  const { yo } = useSesion()
  const [abierta, setAbierta] = useState(false)
  const [usuario, setUsuario] = useState('')
  const [nombre, setNombre] = useState('')
  const [correo, setCorreo] = useState('')
  const [alcanceId, setAlcanceId] = useState<number | null>(null)
  const [esAdmin, setEsAdmin] = useState(false)
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creada, setCreada] = useState<{ usuario: string; clave: string } | null>(null)

  const crear = useCallback(async () => {
    setOcupado(true)
    setError(null)
    try {
      const r = await enviar<{ id: number; usuario: string; clave_temporal?: string }>(
        '/api/admin/usuarios',
        { usuario, nombre, correo, alcance_id: alcanceId, es_admin: esAdmin },
        undefined,
        { headers: { 'x-csrf': yo?.csrf ?? '' } },
      )
      // El nombre sale de la RESPUESTA, no de reconstruirlo aquí: el servidor normaliza con
      // `casefold()` y el equivalente del navegador (`toLowerCase()`) no da lo mismo en todos los
      // alfabetos. Enseñar una contraseña temporal junto a un nombre de cuenta que no existe es
      // la peor forma de equivocarse en esta caja, porque no hay segunda oportunidad de leerla.
      setCreada(r.clave_temporal ? { usuario: r.usuario, clave: r.clave_temporal } : null)
      setUsuario(''); setNombre(''); setCorreo(''); setAlcanceId(null); setEsAdmin(false)
      setAbierta(false)
      await alRecargar()
    } catch (e) {
      setError(mensajeDe(e))
    } finally {
      setOcupado(false)
    }
  }, [usuario, nombre, correo, alcanceId, esAdmin, yo, alRecargar])

  return (
    <div className="nueva-cuenta">
      {creada && (
        <ClaveTemporal usuario={creada.usuario} clave={creada.clave}
                       alCerrar={() => setCreada(null)} />
      )}
      {!abierta ? (
        <button type="button" className="accion nc-abrir" onClick={() => setAbierta(true)}>
          <IconoMas /> Crear una cuenta
        </button>
      ) : (
        <form className="nc-forma"
              onSubmit={(e) => { e.preventDefault(); void crear() }}>
          <div className="nc-campos">
            <label className="campo">
              <span className="rotulo">Usuario</span>
              <input value={usuario} onChange={(e) => setUsuario(e.target.value)}
                     autoComplete="off" required autoFocus />
            </label>
            <label className="campo">
              <span className="rotulo">Nombre (opcional)</span>
              <input value={nombre} onChange={(e) => setNombre(e.target.value)}
                     autoComplete="off" />
            </label>
            <label className="campo">
              <span className="rotulo">Correo (opcional)</span>
              <input type="email" value={correo} onChange={(e) => setCorreo(e.target.value)}
                     autoComplete="off" />
            </label>
            <label className="campo">
              <span className="rotulo">Alcance de datos</span>
              {/* Deshabilitado para un administrador: ve todo por definición, y guardarle un
                  alcance se leería después como «está restringido» cuando no lo está. El
                  servidor rechaza la combinación de todas formas. */}
              <select value={alcanceId ?? ''} disabled={esAdmin}
                      onChange={(e) => setAlcanceId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Ve todo (sin alcance)</option>
                {alcances.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </label>
          </div>
          <label className="adm-opcion">
            <input type="checkbox" checked={esAdmin} disabled={alcanceId != null}
                   onChange={(e) => setEsAdmin(e.target.checked)} />
            <span>Puede administrar (crear cuentas y repartir permisos)</span>
          </label>
          {alcanceId != null && (
            <p className="nc-nota">
              Con alcance, la cuenta nace solo con lo necesario para su categoría: ver las
              oportunidades, analizar sus pliegos y subir su RUP. El asistente, la demanda futura
              y el historial por NIT nacen cerrados; se abren después desde «Editar permisos»
              (el asistente nunca, mientras tenga alcance).
            </p>
          )}
          {error && <p className="admin-error" role="alert">{error}</p>}
          <div className="nc-acciones">
            <button type="submit" className="boton-primario" disabled={ocupado || !usuario.trim()}>
              {ocupado ? 'Creando…' : 'Crear la cuenta'}
            </button>
            <button type="button" className="accion"
                    onClick={() => { setAbierta(false); setError(null) }}>
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

function Cuenta({ u, caps, alcances, alRecargar }: {
  u: UsuarioAdmin
  caps: Cap[]
  alcances: AlcanceAdmin[]
  alRecargar: () => Promise<void>
}) {
  const { yo } = useSesion()
  const soyYo = yo?.usuario === u.usuario
  const [editando, setEditando] = useState(false)
  const [ocupado, setOcupado] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [temporal, setTemporal] = useState<string | null>(null)
  // Reponer la clave cierra las sesiones de la cuenta: un clic de más molesta de verdad, así que
  // el botón pide un segundo clic en vez de abrir un diálogo.
  const [confirmaClave, setConfirmaClave] = useState(false)

  const cabeceras = { headers: { 'x-csrf': yo?.csrf ?? '' } }

  /** Ejecuta una escritura y recarga la lista. Devuelve si FUNCIONÓ: quien la llama decide con
   *  eso si cerrar su formulario — cerrarlo sobre un error tiraría lo que se estaba editando. */
  const accion = useCallback(async (fn: () => Promise<unknown>): Promise<boolean> => {
    setOcupado(true)
    setError(null)
    try {
      await fn()
      await alRecargar()
      return true
    } catch (e) {
      setError(mensajeDe(e))
      return false
    } finally {
      setOcupado(false)
    }
  }, [alRecargar])

  return (
    <li className={`cuenta${u.activo ? '' : ' inactiva'}`}>
      <div className="cuenta-quien">
        <h2>
          {u.usuario}
          {u.es_admin && <span className="etiqueta et-admin">Administra</span>}
          {!u.activo && <span className="etiqueta">Inactiva</span>}
          {u.cambiar_clave && <span className="etiqueta et-ojo">Clave temporal</span>}
        </h2>
        <p className="cuenta-meta">
          {[normalizarNombre(u.nombre) || null,
            u.correo || null,
            u.empresas ? `${entero(u.empresas)} ${u.empresas === 1 ? 'empresa' : 'empresas'}` : null,
            u.ultimo_acceso
              ? `último acceso ${fecha(new Date(u.ultimo_acceso * 1000).toISOString())}`
              : 'nunca ha entrado'].filter(Boolean).join(' · ')}
        </p>
        {u.alcance && (
          <p className="cuenta-alcance">
            <IconoCandado /> Solo ve «{u.alcance.nombre}»
          </p>
        )}
      </div>

      <div className="cuenta-caps">
        {caps.map((c) => {
          const puede = u.caps[c.clave]
          // El caso que hay que DECIR: la casilla está marcada y el permiso no existe.
          const bloqueada = !puede && u.caps_guardadas[c.clave]
          return (
            <span key={c.clave}
                  className={`cap${puede ? ' si' : bloqueada ? ' bloqueada' : ''}`}
                  title={bloqueada
                    ? `${c.titulo} — bloqueada por el alcance de datos`
                    : c.titulo}>
              {c.titulo}
              {c.cuesta && <b className="cap-cuesta" title="gasta tokens en cada uso">·$</b>}
            </span>
          )
        })}
      </div>

      <div className="cuenta-acciones">
        <button type="button" className={`accion${editando ? ' puesta' : ''}`}
                onClick={() => { setEditando(!editando); setError(null) }}>
          {editando ? 'Cerrar sin guardar' : 'Editar permisos'}
        </button>
        {/* Sobre la propia cuenta se deshabilita, igual que «Desactivar»: reponérsela revocaría la
            sesión con la que se está leyendo la respuesta, y esa respuesta trae la única copia de
            la temporal. El servidor lo rechaza también; aquí no se ofrece un botón que solo puede
            terminar mal. */}
        {!confirmaClave ? (
          <button type="button" className="accion" disabled={ocupado || soyYo}
                  title={soyYo
                    ? 'Tu propia contraseña no se repone desde aquí: cerraría tu sesión antes de que pudieras copiar la temporal'
                    : undefined}
                  onClick={() => setConfirmaClave(true)}>
            Reponer la clave
          </button>
        ) : (
          <button type="button" className="accion peligro" disabled={ocupado}
                  onClick={() => {
                    setConfirmaClave(false)
                    void accion(async () => {
                      const r = await enviar<{ clave_temporal: string }>(
                        `/api/admin/usuarios/${u.id}/clave`, {}, undefined, cabeceras)
                      setTemporal(r.clave_temporal)
                    })
                  }}>
            Se cerrarán sus sesiones — confirmar
          </button>
        )}
        <button type="button" className={`accion${u.activo ? ' peligro' : ''}`}
                disabled={ocupado || soyYo}
                title={soyYo ? 'Tu propia cuenta no se desactiva desde aquí' : undefined}
                onClick={() => void accion(() =>
                  enviar(`/api/admin/usuarios/${u.id}/estado`,
                         { activo: !u.activo }, undefined, cabeceras))}>
          {u.activo ? 'Desactivar' : 'Reactivar'}
        </button>
      </div>

      {temporal && (
        <ClaveTemporal usuario={u.usuario} clave={temporal} alCerrar={() => setTemporal(null)} />
      )}
      {error && <p className="admin-error" role="alert">{error}</p>}
      {editando && (
        <EditorPermisos u={u} caps={caps} alcances={alcances} ocupado={ocupado}
                        alGuardar={(cuerpo) => {
                          void accion(() =>
                            enviar(`/api/admin/usuarios/${u.id}/permisos`,
                                   cuerpo, undefined, cabeceras),
                          ).then((funciono) => { if (funciono) setEditando(false) })
                        }} />
      )}
    </li>
  )
}

/** El formulario de permisos edita las casillas GUARDADAS —lo que hay escrito—, no las efectivas:
 *  es lo que se va a escribir de vuelta. La diferencia con lo efectivo (el chat bloqueado por el
 *  alcance) se dice en el propio formulario en vez de fingir que la casilla no existe. */
function EditorPermisos({ u, caps, alcances, ocupado, alGuardar }: {
  u: UsuarioAdmin
  caps: Cap[]
  alcances: AlcanceAdmin[]
  ocupado: boolean
  alGuardar: (cuerpo: { es_admin: boolean; alcance_id: number | null; caps: Record<string, boolean> }) => void
}) {
  const [esAdmin, setEsAdmin] = useState(u.es_admin)
  const [alcanceId, setAlcanceId] = useState<number | null>(u.alcance?.id ?? null)
  const [marcadas, setMarcadas] = useState<Record<string, boolean>>({ ...u.caps_guardadas })

  return (
    <form className="editor-permisos"
          onSubmit={(e) => {
            e.preventDefault()
            // Se manda lo que la pantalla ESTÁ enseñando. Con alcance, la casilla del asistente se
            // dibuja apagada, así que mandar el `true` que quedó en el estado sería pedir algo
            // distinto de lo que se ve. El servidor lo corrige igual —el invariante vive en
            // `store`—, pero un cliente que pide una cosa y muestra otra es el origen del gemelo.
            const caps = alcanceId != null ? { ...marcadas, chat: false } : marcadas
            alGuardar({ es_admin: esAdmin, alcance_id: alcanceId, caps })
          }}>
      <div className="ep-filas">
        <label className="campo">
          <span className="rotulo">Alcance de datos</span>
          <select value={alcanceId ?? ''} disabled={esAdmin}
                  onChange={(e) => setAlcanceId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Ve todo (sin alcance)</option>
            {alcances.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </label>
        <label className="adm-opcion">
          <input type="checkbox" checked={esAdmin} disabled={alcanceId != null}
                 onChange={(e) => setEsAdmin(e.target.checked)} />
          <span>Puede administrar</span>
        </label>
      </div>

      <fieldset className="ep-caps">
        <legend className="rotulo">Capacidades</legend>
        {caps.map((c) => {
          const esChatBloqueado = c.clave === 'chat' && alcanceId != null
          return (
            <label key={c.clave}
                   className={`adm-opcion${esChatBloqueado ? ' apagada' : ''}`}
                   title={esChatBloqueado
                     ? 'Con alcance de datos el asistente queda cerrado: consulta el grafo completo y anularía la restricción'
                     : undefined}>
              <input type="checkbox"
                     checked={esChatBloqueado ? false : !!marcadas[c.clave]}
                     disabled={esChatBloqueado || esAdmin}
                     onChange={(e) => setMarcadas({ ...marcadas, [c.clave]: e.target.checked })} />
              <span>
                {c.titulo}
                {c.cuesta && <b className="cap-cuesta" title="gasta tokens en cada uso">·$</b>}
              </span>
            </label>
          )
        })}
        {esAdmin && (
          <p className="ep-nota">Quien administra puede todo: las casillas no aplican.</p>
        )}
      </fieldset>

      <button type="submit" className="boton-primario" disabled={ocupado}>
        {ocupado ? 'Guardando…' : 'Guardar los permisos'}
      </button>
    </form>
  )
}

/* ── alcances ─────────────────────────────────────────────────────────────── */

function Alcances({ alcances }: { alcances: AlcanceAdmin[] }) {
  return (
    <>
      <div className="admin-regla">
        <span className="rotulo">Cómo se lee un alcance</span>
        <p>
          Un proceso entra si casa con <b>alguna</b> de las inclusiones —código UNSPSC, palabra en
          el objeto, o sector— y no casa con ninguna exclusión. Las restricciones (departamento,
          modalidad, valor) <b>acotan</b> lo que ya entró. Un alcance sin inclusiones no muestra
          nada.
        </p>
      </div>
      {alcances.length === 0 ? (
        <p className="nota">
          No hay alcances definidos, así que todas las cuentas ven todo el universo.
        </p>
      ) : (
        <ul className="alc-lista">
          {alcances.map((a) => <Alcance key={a.id} a={a} />)}
        </ul>
      )}
    </>
  )
}

function Alcance({ a }: { a: AlcanceAdmin }) {
  const [p, setP] = useState<Prueba | null>(null)
  const [probando, setProbando] = useState(false)
  const inc = a.filtro.incluir ?? {}
  const exc = a.filtro.excluir ?? {}

  const probar = useCallback(async () => {
    setProbando(true)
    try {
      setP(await enviar<Prueba>('/api/admin/alcances/probar',
                                { filtro: a.filtro, nombre: a.nombre }))
    } catch {
      setP(null)
    } finally {
      setProbando(false)
    }
  }, [a])

  /* La alerta de RUIDO. Si casi todo entra por palabra, el alcance está casando con el texto libre
     del objeto y no con el código: es donde se cuela lo que no debería verse. Es la lectura que
     convierte el desglose en una herramienta y no en tres números. */
  const porPalabra = p?.vias?.palabra ?? 0
  const conVia = Object.values(p?.vias ?? {}).reduce((n, v) => n + v, 0)
  const ruidoso = conVia >= 5 && porPalabra / conVia >= 0.8

  return (
    <li className="alc">
      <div className="alc-quien">
        <h2>{a.nombre}</h2>
        {a.descripcion && <p className="alc-desc">{a.descripcion}</p>}
        <p className="alc-cuentas">
          {a.cuentas
            ? `${entero(a.cuentas)} ${a.cuentas === 1 ? 'cuenta lo usa' : 'cuentas lo usan'}`
            : 'Ninguna cuenta lo usa'}
        </p>
      </div>

      <dl className="alc-filtro">
        <Parte k="Códigos UNSPSC" v={inc.unspsc} />
        <Parte k="Palabras en el objeto" v={inc.texto} />
        <Parte k="Sectores" v={inc.sectores} />
        <Parte k="Excluye por código" v={exc.unspsc} />
        <Parte k="Excluye por palabra" v={exc.texto} />
      </dl>

      <div className="alc-prueba">
        <button type="button" className="accion" onClick={() => void probar()} disabled={probando}>
          {probando ? 'Probando…' : 'Probar sobre los abiertos'}
        </button>
        {p && (
          <>
            <p className="alc-resultado">
              <b>{entero(p.total)}</b> de {entero(p.universo)} procesos abiertos entran
              {conVia > 0 && (
                <> — {Object.entries(p.vias)
                  .map(([k, v]) => `${entero(v)} por ${k}`)
                  .join(' · ')}</>
              )}
              {p.muestra < p.total && (
                <span className="alc-muestra">
                  {' '}(el desglose es sobre {entero(p.muestra)} de muestra)
                </span>
              )}
            </p>
            {ruidoso && (
              <p className="alc-ruido">
                Casi todo entra <b>por palabra</b>, no por código. Eso casa con el texto libre del
                objeto, que es por donde se cuela lo que no debería verse: revisa si alguna palabra
                es demasiado general.
              </p>
            )}
            {p.total === 0 && (
              <p className="alc-ruido">
                <b>Ningún proceso abierto entra.</b> Quien tenga este alcance ve una pantalla vacía.
              </p>
            )}
          </>
        )}
      </div>
    </li>
  )
}

function Parte({ k, v }: { k: string; v?: string[] | undefined }) {
  if (!v?.length) return null
  return (
    <div>
      <dt>{k}</dt>
      <dd>{v.join(' · ')}</dd>
    </div>
  )
}

/* ── uso y costos ─────────────────────────────────────────────────────────── */

function UsoYCostos({ uso }: { uso: Uso | null }) {
  if (!uso?.hay_registro) {
    return (
      <p className="nota">
        No hay registro de costos. Se escribe en <code>logs/cost.log</code> cada vez que se llama a
        un modelo; si el archivo no existe todavía es que no se ha llamado a ninguno desde este
        despliegue.
      </p>
    )
  }
  return (
    <>
      <div className="uso-hero">
        <span className="cifra">US$ {(uso.usd ?? 0).toFixed(2)}</span>
        <span>en los últimos {entero(uso.dias ?? 30)} días</span>
      </div>
      <p className="nota">
        {entero(uso.lineas ?? 0)} llamadas al modelo
        {uso.usd_por_llamada != null && <> · US$ {uso.usd_por_llamada.toFixed(4)} de media</>}
        {uso.cache_hit_pct != null && <> · {porcentaje(uso.cache_hit_pct, { decimales: 1 })} de
          caché aprovechada</>}
      </p>
      <dl className="uso-modelos">
        {Object.entries(uso.por_modelo ?? {}).map(([m, v]) => (
          <div key={m}>
            <dt>{m}</dt>
            <dd className="cifra">US$ {v.toFixed(2)}</dd>
          </div>
        ))}
      </dl>
      {/* ⚠ La procedencia y su límite. Un total sospechosamente bajo puede ser «se gastó poco» o
          «faltan datos», y sin decirlo se lee siempre como lo primero. */}
      <p className="descargo">
        Sale de <code>logs/cost.log</code>, que es un archivo de líneas y no una base: si se rota o
        se pierde, aquí no hay historia que reconstruir. No hay tope de gasto — esta pantalla
        informa, no corta.
      </p>
    </>
  )
}

/* ── lateral ──────────────────────────────────────────────────────────────── */

function EstadoDeLosDatos({ datos }: { datos: EstadoDatos | null }) {
  return (
    <div className="admin-caja">
      <span className="rotulo">Estado de los datos</span>
      {!datos ? <p className="nota">No pude leer el cubo.</p> : (
        <>
          <dl className="admin-datos">
            <Dato k="Generado" v={datos.generado_en ? fecha(datos.generado_en.slice(0, 10)) : '—'} />
            <Dato k="Corte del grafo" v={datos.corte_grafo ? fecha(datos.corte_grafo) : '—'} />
            <Dato k="Procesos abiertos" v={entero(datos.procesos ?? null)} />
            <Dato k="Entidades" v={entero(datos.entidades ?? null)} />
            <Dato k="Nuevos" v={entero(datos.nuevos ?? null)} />
            <Dato k="Con cambios" v={entero(datos.cambiados ?? null)} />
          </dl>
          {/* Que el match semántico esté apagado NO es un detalle de infraestructura: sin él la
              afinidad cae a la rama por sector y nadie lo nota mirando la bandeja. */}
          {datos.semantica && <AvisoSemantica s={datos.semantica} />}
        </>
      )}
    </div>
  )
}

function ResumenUso({ uso }: { uso: Uso | null }) {
  if (!uso?.hay_registro) return null
  return (
    <div className="admin-caja">
      <span className="rotulo">Gasto de IA</span>
      <p className="uso-mini">
        <b className="cifra">US$ {(uso.usd ?? 0).toFixed(2)}</b>
        <span>últimos {entero(uso.dias ?? 30)} días</span>
      </p>
      <p className="nota">
        {entero(uso.lineas ?? 0)} llamadas. Sin tope configurado.
      </p>
    </div>
  )
}

function Dato({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt>{k}</dt>
      <dd className="cifra">{v}</dd>
    </div>
  )
}
